import { supabase } from '@/lib/supabase'
import { profileService } from './profileService'
import { supplierService } from './supplierService'
import { componentService } from './componentService'
import { productService } from './productService'
import { checklistService } from './checklistService'
import { qmsActionService } from './qmsActionService'
import { nonconformanceService } from './nonconformanceService'
import { documentService } from './documentService'
import { documentSearchService, type DocumentContext } from './documentSearchService'
import { callAIProxy } from './aiProxyClient'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

export interface ChatSession {
  id: string
  title: string
  messages: ChatMessage[]
  createdAt: Date
  updatedAt: Date
}

interface SystemContext {
  suppliers: {
    total: number
    approved: number
    pending: number
    issues: Array<{ name: string; status: string; reason?: string }>
  }
  components: {
    total: number
    ceMarked: number
    ukcaMarked: number
    lowStock: Array<{ name: string; partNumber: string; stockLevel?: number }>
    topReused: Array<{ name: string; partNumber: string; productsUsing: number }>
    sharedCount: number
    uniqueCount: number
  }
  products: {
    total: number
    items: Array<{ name: string; sku: string; status: string; componentCount?: number }>
  }
  checklists: {
    total: number
    inProgress: number
    completed: number
    pending: Array<{ name: string; progress: number }>
  }
  actions: {
    total: number
    overdue: number
    upcoming: Array<{ title: string; dueDate: string; priority: string }>
  }
  nonconformances: {
    total: number
    open: number
    critical: Array<{ title: string; status: string; severity: string }>
  }
  documents: {
    total: number
    expiringSoon: Array<{ name: string; expiryDate: string }>
  }
}

async function gatherSystemContext(): Promise<SystemContext> {
  try {
    // Gather data from various services
    const [suppliers, components, products, checklists, actions, ncrs, documents, usageSummary] = await Promise.all([
      supplierService.getSuppliers().catch(() => []),
      componentService.getComponents().catch(() => []),
      productService.getProducts().catch(() => []),
      checklistService.getChecklists().catch(() => []),
      qmsActionService.getActions().catch(() => []),
      nonconformanceService.getNonconformances().catch(() => []),
      documentService.getDocuments().catch(() => []),
      componentService.getComponentUsageSummary().catch(() => []),
    ])

    // Process suppliers - use explicit type checking for null values
    // SupplierStatus: 'active' | 'inactive' | 'pending_review' | 'suspended'
    const supplierIssues = suppliers
      .filter(s => s.status === 'pending_review' || s.status === 'suspended' || s.status === 'inactive')
      .map(s => ({ name: s.name, status: s.status ?? 'unknown' }))

    // Process components (check for low stock if applicable)
    const lowStockComponents = components
      .filter(c => 
        c.stock_quantity != null && 
        c.min_stock_level != null && 
        c.stock_quantity < c.min_stock_level
      )
      .map(c => ({ 
        name: c.name, 
        partNumber: c.part_number ?? '', 
        stockLevel: c.stock_quantity ?? undefined
      }))

    // Process checklists
    const pendingChecklists = checklists
      .filter(cl => cl.status === 'in_progress' || cl.status === 'not_started')
      .map(cl => ({ 
        name: cl.name ?? 'Unnamed', 
        progress: cl.progress ?? 0 
      }))

    // Process actions
    const now = new Date()
    const upcomingActions = actions
      .filter(a => 
        a.status !== 'completed' && 
        a.due_date
      )
      .map(a => ({
        title: a.title ?? a.description ?? 'Untitled',
        dueDate: a.due_date ?? '',
        priority: a.priority ?? 'medium',
      }))
      .slice(0, 10)

    const overdueActions = actions.filter(a => 
      a.status !== 'completed' && 
      a.due_date && 
      new Date(a.due_date) < now
    ).length

    // Process NCRs
    const criticalNcrs = ncrs
      .filter(n => 
        n.status !== 'closed' && 
        (n.severity === 'critical' || n.severity === 'major')
      )
      .map(n => ({
        title: n.title ?? n.description ?? 'Untitled',
        status: n.status ?? 'open',
        severity: n.severity ?? 'unknown',
      }))

    // Process documents for expiring soon
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000)
    const expiringDocs = documents
      .filter(d => 
        d.expiry_date && 
        new Date(d.expiry_date) < thirtyDaysFromNow &&
        new Date(d.expiry_date) > now
      )
      .map(d => ({
        name: d.name ?? 'Untitled',
        expiryDate: d.expiry_date ?? '',
      }))

    // Component reuse intelligence
    const topReused = usageSummary
      .filter(u => u.products_using_count > 1)
      .sort((a, b) => b.products_using_count - a.products_using_count)
      .slice(0, 10)
      .map(u => ({
        name: u.component_name,
        partNumber: u.part_number,
        productsUsing: u.products_using_count,
      }))

    const sharedCount = usageSummary.filter(u => u.products_using_count > 1).length
    const uniqueCount = usageSummary.filter(u => u.products_using_count <= 1).length

    return {
      suppliers: {
        total: suppliers.length,
        approved: suppliers.filter(s => s.status === 'active').length,
        pending: suppliers.filter(s => s.status === 'pending_review').length,
        issues: supplierIssues,
      },
      components: {
        total: components.length,
        ceMarked: components.filter(c => c.ce_marked === true).length,
        ukcaMarked: components.filter(c => c.ukca_marked === true).length,
        lowStock: lowStockComponents,
        topReused,
        sharedCount,
        uniqueCount,
      },
      products: {
        total: products.length,
        items: products.slice(0, 10).map(p => ({
          name: p.name,
          sku: p.sku ?? '',
          status: p.status ?? 'active'
        })),
      },
      checklists: {
        total: checklists.length,
        inProgress: checklists.filter(cl => cl.status === 'in_progress').length,
        completed: checklists.filter(cl => cl.status === 'completed').length,
        pending: pendingChecklists.slice(0, 5),
      },
      actions: {
        total: actions.length,
        overdue: overdueActions,
        upcoming: upcomingActions,
      },
      nonconformances: {
        total: ncrs.length,
        open: ncrs.filter(n => n.status !== 'closed').length,
        critical: criticalNcrs,
      },
      documents: {
        total: documents.length,
        expiringSoon: expiringDocs,
      },
    }
  } catch (error) {
    console.error('Error gathering system context:', error)
    return {
      suppliers: { total: 0, approved: 0, pending: 0, issues: [] },
      components: { total: 0, ceMarked: 0, ukcaMarked: 0, lowStock: [], topReused: [], sharedCount: 0, uniqueCount: 0 },
      products: { total: 0, items: [] },
      checklists: { total: 0, inProgress: 0, completed: 0, pending: [] },
      actions: { total: 0, overdue: 0, upcoming: [] },
      nonconformances: { total: 0, open: 0, critical: [] },
      documents: { total: 0, expiringSoon: [] },
    }
  }
}

function buildSystemPrompt(context: SystemContext, documentContext?: DocumentContext | null): string {
  const documentSection = documentContext && documentContext.documents.length > 0
    ? `

## RELEVANT DOCUMENTS
The following documents from the user's document library are relevant to their question. Reference specific documents by name when answering. If you cite information from a document, mention which document it came from.

${documentContext.contextText}

---
`
    : ''

  return `You are the compliance intelligence assistant for LogiDocs Certify — a platform that manages CE/UKCA Marking and ISO Standards for SME manufacturers. You answer questions about the user's QMS data, suggest next actions, and help prepare for audits.

ROLE: Act as a knowledgeable compliance adviser who has full visibility of the user's QMS. Be direct, cite specifics, and always ground answers in the data below.

LANGUAGE: Use British English (behaviour, colour, organisation, etc.).
${documentSection}

## Live QMS Data

SUPPLIERS: ${context.suppliers.total} total | ${context.suppliers.approved} active | ${context.suppliers.pending} pending review${context.suppliers.issues.length > 0 ? `\nFLAGGED SUPPLIERS: ${context.suppliers.issues.map(s => `${s.name} (${s.status})`).join('; ')}` : ''}

COMPONENTS: ${context.components.total} total | ${context.components.ceMarked} CE marked | ${context.components.ukcaMarked} UKCA marked | ${context.components.sharedCount} shared across products | ${context.components.uniqueCount} unique${context.components.lowStock.length > 0 ? `\nLOW STOCK: ${context.components.lowStock.map(c => `${c.name} (qty: ${c.stockLevel ?? '?'})`).join('; ')}` : ''}${context.components.topReused.length > 0 ? `\nMOST REUSED: ${context.components.topReused.map(c => `${c.name} [${c.partNumber}] → ${c.productsUsing} products`).join('; ')}` : ''}

PRODUCTS: ${context.products.total} total${context.products.items.length > 0 ? ` — ${context.products.items.map(p => `${p.name} (${p.sku || 'no SKU'}, ${p.status})`).join('; ')}` : ''}

CHECKLISTS: ${context.checklists.total} total | ${context.checklists.inProgress} in progress | ${context.checklists.completed} completed${context.checklists.pending.length > 0 ? `\nINCOMPLETE: ${context.checklists.pending.map(cl => `${cl.name} (${cl.progress}%)`).join('; ')}` : ''}

ACTIONS: ${context.actions.total} total | ${context.actions.overdue} overdue${context.actions.upcoming.length > 0 ? `\nUPCOMING: ${context.actions.upcoming.map(a => `${a.title} (due ${a.dueDate}, ${a.priority})`).join('; ')}` : ''}

NCRs: ${context.nonconformances.total} total | ${context.nonconformances.open} open${context.nonconformances.critical.length > 0 ? `\nCRITICAL/MAJOR: ${context.nonconformances.critical.map(n => `${n.title} — ${n.severity}, ${n.status}`).join('; ')}` : ''}

DOCUMENTS: ${context.documents.total} total${context.documents.expiringSoon.length > 0 ? `\nEXPIRING WITHIN 30 DAYS: ${context.documents.expiringSoon.map(d => `${d.name} (${d.expiryDate})`).join('; ')}` : ''}

## Response Rules

1. Lead with the answer, then supporting detail. Keep responses concise — aim for 2-4 short paragraphs max.
2. Always cite specifics: part numbers, document names, product names, dates, counts.
3. When asked "what needs doing" or "what's next", triage by severity:
   (a) overdue actions & critical NCRs → (b) expiring documents → (c) pending supplier reviews → (d) incomplete checklists → (e) low stock components.
4. COMPONENT IMPACT: When a user asks about changing, replacing, or reviewing a component, immediately flag every product that uses it and the total count. Format: "⚠ [Part Number] is used in N products: [list]. Changes require: compliance doc review, retest scope assessment, supplier notification."
5. SHARED PARTS: When asked about common/shared parts, reference the reuse data above with exact counts and product names.
6. DOCUMENT EVIDENCE: If the RELEVANT DOCUMENTS section is populated, ground your answer in those documents. Cite by name: "Per [Document Name], …"
7. If asked about data you don't have, say so clearly and suggest where the user might find it.
8. Suggest concrete next steps when the data warrants it — link actions to specific records.
9. Never fabricate data. Only reference what appears in the Live QMS Data above.`
}

export const aiAssistantService = {
  /**
   * Send a message to the AI assistant
   * Supports both OpenAI and OpenRouter APIs
   */
  async sendMessage(
    message: string,
    chatHistory: ChatMessage[],
    options?: { enableRAG?: boolean; onSearchingDocuments?: () => void }
  ): Promise<{ response: string; sources: Array<{ id: string; name: string; type: string | null }> }> {
    // Gather current system context
    const context = await gatherSystemContext()

    // Search documents for RAG context (if enabled)
    let documentContext: DocumentContext | null = null
    const enableRAG = options?.enableRAG !== false // default: on
    if (enableRAG) {
      try {
        options?.onSearchingDocuments?.()
        documentContext = await documentSearchService.buildRAGContext(message, 6000)
      } catch (error) {
        console.warn('Document search failed (non-blocking):', error)
      }
    }

    const systemPrompt = buildSystemPrompt(context, documentContext)

    // Build messages array
    const messages = [
      { role: 'system' as const, content: systemPrompt },
      ...chatHistory.slice(-10).map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
      })),
      { role: 'user' as const, content: message },
    ]

    try {
      // All AI calls go through the secure server-side proxy. The provider key,
      // model choice and token accounting are handled server-side.
      const { content } = await callAIProxy({
        operation: 'chat',
        messages,
      })

      const responseText = content || 'Sorry, I could not generate a response.'

      // Extract sources from document context
      const sources = documentContext?.documents?.map(doc => ({
        id: doc.id,
        name: doc.name,
        type: doc.type,
      })) ?? []

      return { response: responseText, sources }
    } catch (error) {
      console.error('AI Assistant error:', error)
      throw error
    }
  },

  /**
   * Save chat session to database
   */
  async saveChatSession(session: ChatSession): Promise<void> {
    const orgId = await profileService.getOrganizationId()
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!orgId || !user) return

    const { error } = await supabase
      .from('chat_sessions')
      .upsert({
        id: session.id,
        organization_id: orgId,
        user_id: user.id,
        title: session.title,
        messages: session.messages,
        created_at: session.createdAt.toISOString(),
        updated_at: new Date().toISOString(),
      })

    if (error) {
      console.error('Error saving chat session:', error)
      throw error
    }
  },

  /**
   * Get chat sessions for current user
   */
  async getChatSessions(): Promise<ChatSession[]> {
    const orgId = await profileService.getOrganizationId()
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!orgId || !user) return []

    const { data, error } = await supabase
      .from('chat_sessions')
      .select('*')
      .eq('organization_id', orgId)
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(20)

    if (error) {
      console.error('Error fetching chat sessions:', error)
      return []
    }

    return (data || []).map((row: any) => ({
      id: row.id,
      title: row.title,
      messages: row.messages as ChatMessage[],
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }))
  },

  /**
   * Delete a chat session
   */
  async deleteChatSession(sessionId: string): Promise<void> {
    const { error } = await supabase
      .from('chat_sessions')
      .delete()
      .eq('id', sessionId)

    if (error) {
      console.error('Error deleting chat session:', error)
      throw error
    }
  },

  /**
   * Generate a title for a chat session based on the first message
   */
  generateSessionTitle(firstMessage: string): string {
    // Truncate and clean up the message for a title
    const cleaned = firstMessage.replace(/\n/g, ' ').trim()
    if (cleaned.length <= 40) return cleaned
    return cleaned.substring(0, 37) + '...'
  },
}
